import type { PricingData } from '../types'
import { useEffect, useRef, useState } from 'react'
import { formatPrice } from '../utils/helpers'

interface FloatingPriceProps {
  pricing: PricingData
  onShowSummary: () => void
  isConfigurationValid?: boolean
  canShowPrice?: boolean
  hasModel?: boolean
  priceMode: 'netto' | 'vat8' | 'vat23'
  setPriceMode: (mode: 'netto' | 'vat8' | 'vat23') => void
}

const FloatingPrice = ({ pricing, onShowSummary, isConfigurationValid = true, canShowPrice = false, hasModel = false, priceMode, setPriceMode }: FloatingPriceProps) => {
  // Show floating price if we have a model, regardless of validation
  if (!hasModel) return null

  const [menuOpen, setMenuOpen] = useState(false)
  const [isHidden, setIsHidden] = useState(false)
  const menuRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!menuRef.current) return
      if (!(e.target instanceof Node)) return
      if (!menuRef.current.contains(e.target)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('click', onDocClick)
    return () => document.removeEventListener('click', onDocClick)
  }, [])

  const handleSummaryClick = () => {
    if (isConfigurationValid) {
      onShowSummary()
    } else {
      // This will be handled by the parent component
      onShowSummary()
    }
  }

  const vatMultiplier = priceMode === 'vat8' ? 1.08 : priceMode === 'vat23' ? 1.23 : 1
  const rawPrice = canShowPrice ? pricing.totalPrice : 0
  const displayPrice = Math.round(rawPrice * vatMultiplier)

  const label = priceMode === 'netto' 
    ? 'CENA NETTO:' 
    : priceMode === 'vat8' 
      ? 'CENA BRUTTO (8% VAT):' 
      : 'CENA BRUTTO (23% VAT):'

  return (
    <div className={`floating-price ${isHidden ? 'hidden' : ''}`} id="floating-price">
      {isHidden && (
        <div className="floating-price-tab" onClick={() => setIsHidden(false)}>
          cena
        </div>
      )}
      <div className="floating-price-content" ref={menuRef}>
        <div className="floating-price-actions">
          <button 
            type="button" 
            className="floating-price-hide" 
            aria-label="Ukryj cenę"
            onClick={() => setIsHidden(true)}
            title="Ukryj cenę"
            style={{ color: '#D1B48D' }}
          >
            −
          </button>
          <button 
            type="button" 
            className="floating-price-gear" 
            aria-label="Opcje ceny"
            onClick={(e) => { e.stopPropagation(); setMenuOpen(v => !v) }}
            title="Opcje ceny"
            style={{ color: '#D1B48D', fontSize: '11px', fontWeight: 'bold' }}
          >
            VAT 8/23
          </button>
          {menuOpen && (
            <div className="floating-price-menu" role="menu">
              <button 
                type="button" 
                className={`menu-item ${priceMode === 'netto' ? 'active' : ''}`} 
                onClick={() => { setPriceMode('netto'); setMenuOpen(false) }}
                role="menuitem"
              >
                {priceMode === 'netto' ? '✓ ' : ''}NETTO
              </button>
              <button 
                type="button" 
                className={`menu-item ${priceMode === 'vat8' ? 'active' : ''}`} 
                onClick={() => { setPriceMode('vat8'); setMenuOpen(false) }}
                role="menuitem"
              >
                {priceMode === 'vat8' ? '✓ ' : ''}8% VAT
              </button>
              <button 
                type="button" 
                className={`menu-item ${priceMode === 'vat23' ? 'active' : ''}`} 
                onClick={() => { setPriceMode('vat23'); setMenuOpen(false) }}
                role="menuitem"
              >
                {priceMode === 'vat23' ? '✓ ' : ''}23% VAT
              </button>
            </div>
          )}
        </div>
        <div className="floating-price-label">{label}</div>
        <div className="floating-price-value" id="floating-total-price">
          {displayPrice > 0 ? formatPrice(displayPrice) + ' zł' : 'Wybierz wariant'}
        </div>
        <div className="floating-price-note">
          {displayPrice > 0 ? '+ transport i montaż' : 'aby zobaczyć cenę'}
        </div>
        <button 
          className={`floating-price-btn ${!isConfigurationValid ? 'disabled' : ''}`} 
          onClick={handleSummaryClick}
          disabled={!isConfigurationValid}
        >
          PODSUMOWANIE
        </button>
      </div>
    </div>
  )
}

export default FloatingPrice
